import type { ReactNode } from 'react'
import sanitizeHtml from 'sanitize-html'
import {
	InPageAlert,
	type InPageAlertType,
} from '../ui/in-page-alert/InPageAlert'

/**
 * Prescription status alerts, rendered with the shared NSW Design System
 * in-page alert (components/ui/in-page-alert/InPageAlert.tsx).
 */
export interface PrescriptionInPageAlertProps {
	as: Extract<InPageAlertType, 'warning' | 'success'>
	title?: string
	children: ReactNode
	'data-testid'?: string
}

export function PrescriptionInPageAlert({
	as,
	title,
	children,
	'data-testid': dataTestId,
}: PrescriptionInPageAlertProps) {
	return (
		<InPageAlert as={as} title={title} data-testid={dataTestId}>
			{children}
		</InPageAlert>
	)
}

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
	allowedTags: [
		'p',
		'ul',
		'ol',
		'li',
		'strong',
		'em',
		'b',
		'i',
		'a',
		'br',
		'span',
	],
	allowedAttributes: {
		a: ['href', 'target', 'rel'],
	},
}

export interface PrescriptionIncompleteAlertProps {
	messages: string[]
}

export function PrescriptionIncompleteAlert({
	messages,
}: PrescriptionIncompleteAlertProps) {
	if (messages.length === 0) return null

	return (
		<PrescriptionInPageAlert
			as="warning"
			title="Selection incomplete"
			data-testid="prescription-incomplete-alert"
		>
			<ul className="list-disc pl-5 mb-0 mt-4">
				{messages.map((msg, idx) => (
					<li key={`${idx}-${msg}`}>{msg}</li>
				))}
			</ul>
		</PrescriptionInPageAlert>
	)
}

export interface PrescriptionConfirmationAlertProps {
	message: string
	courseTitle: string
}

export function PrescriptionConfirmationAlert({
	message,
	courseTitle,
}: PrescriptionConfirmationAlertProps) {
	const html = sanitizeHtml(message ?? '', SANITIZE_OPTIONS).trim()

	return (
		<PrescriptionInPageAlert
			as="success"
			title="Selection complete"
			data-testid="prescription-confirmation-alert"
		>
			{html ? (
				<div
					className="richtext mt-4"
					dangerouslySetInnerHTML={{ __html: html }}
				/>
			) : (
				<div className="mt-4">
					{`Prescribed text selection meets the requirements for ${courseTitle}`}
				</div>
			)}
		</PrescriptionInPageAlert>
	)
}
